/**
 * Central Redux store configuration for the MedOnc dashboard.
 *
 * Aggregates all feature slices (orders, patients, configurations, etc.)
 * and exports typed dispatch, state, and thunk helpers.
 * @module store
 */

import { Action, configureStore, ThunkAction } from "@reduxjs/toolkit";
import allCoveragesForOrderSlice from "../slice/allCoveragesForOrderSlice";
import allCptSlice from "../slice/allCpt-Slice";
import analyticsDataSlice from "../slice/analyticsDataSlice";
import auditTrialOverviewSlice from "../slice/auditTrialOverviewSlice";
import auditTrialSlice from "../slice/auditTrialSlice";
import firebaseAuthSlice from "../slice/auth-slice";
import authStatusOptionsSlice from "../slice/authStatusOptionsSlice";
import boOptionsSlice from "../slice/boOptionsSlice";
import estimateOptionsSlice from "../slice/estimateOptionsSlice";
import clientConfigurationSlice from "../slice/clientConfiguration";
import cmmDiffDataSlice from "../slice/cmm/cmmDiffDataSlice";
import cmmFormConfig from "../slice/cmm/cmmFormConfig";
import cmmFormDiffTableDataSlice from "../slice/cmm/cmmFormDiffTableDataSlice";
import cmmOrdersSlice from "../slice/cmm/cmmOrdersSlice";
import cmmProcessedOrdersSlice from "../slice/cmm/cmmProcessedOrdersSlice";
import cmmSingleOrderSlice from "../slice/cmm/cmmSingleOrderSlice";
import diagnosisDetailsSlice from "../slice/cmm/dignosisDetailsSlice";
import insuranceDetailsSlice from "../slice/cmm/insuranceDetailsSlice";
import patientEligibilitySlice from "../slice/cmm/patientEligibilitySlice";
import pharmaQuestionsSlice from "../slice/cmm/pharmaQuestionsSlice";
import prescriptionSlice from "../slice/cmm/prescriptionSlice";
import uniqueDrugnameSlice from "../slice/cmm/uniqueDrugnameSlice";
import uniqueFormNameSlice from "../slice/cmm/uniqueFormNameSlice";
import nycbsSentToPlanSlice from "../slice/configurations/nycbsSentToPlanSlice";
import counterSlice from "../slice/counter-slice";
import { credentialsConfigSlice } from "../slice/credentialConfigSlice";
import credentialDetailsSlice from "../slice/credentialDetailsSlice";
import documentsConfigurationSlice from "../slice/documentsConfiguration";
import drugConfigSlice from "../slice/drugConfigSlice";
import facilitySlice from "../slice/facilitySlice";
import fileUploadSlice from "../slice/fileUploadSlice";
import filterValuesSlice from "../slice/filterValuesSlice";
import availityServiceReviewConfigurationSlice from "../slice/form-slice";
import formConfigNoLiveListenSlice from "../slice/formConfigNoLiveListenSlice";
import formConfigSlice from "../slice/formConfigSlice";
import formOptionsSlice from "../slice/formOptionsSlice";
import formQuestionsSlice from "../slice/formQuestions-slice";
import healthFacilitySlice from "../slice/healthFacilitySlice";
import algoliaFacetsCountSlice from "../slice/medicalPa/algoliaFacetsCountSlice";
import algoliaMultiFacetsCountSlice from "../slice/medicalPa/algoliaMultiFacetsCountSlice";
import authOnFileMedicalPaOrdersSlice from "../slice/medicalPa/authOnFileSlice";
import authorizedMedicalPaOrdersSlice from "../slice/medicalPa/authorizedPaOrdersSlice";
import deniedMedicalPaOrderSlice from "../slice/medicalPa/deniedPaOrders";
import externalTablePoweredByAlgoliaSlice from "../slice/medicalPa/externalTablePoweredByAlgoliaSlice";
import finalWorklistOrdersSlice from "../slice/medicalPa/finalWorklistSlice";
import financialMedicalPaOrdersSlice from "../slice/medicalPa/financialPaOrdersSlice";
import genericAuthStatusTableSlice from "../slice/medicalPa/genericAuthStatusTableSlice";
import { guidelinesDocumentsSlice } from "../slice/medicalPa/guidelinesDocumentsSlice";
import holdMedicalPaOrderSlice from "../slice/medicalPa/holdPaOrdersSlice";
import medicalPaClinicalDataSlice from "../slice/medicalPa/medicalPaClinicalDataSlice";
import medicalPaFormConfigSlice from "../slice/medicalPa/medicalPaFormConfig";
import medicalPaOrdersAlgoliaSlice from "../slice/medicalPa/medicalPaOrdersAlgoliaSlice";
import medicalPaOrdersFromPostgresSlice from "../slice/medicalPa/medicalPaOrdersFromPostgres";
import medicalPaOrdersSlice from "../slice/medicalPa/medicalPaOrdersSlice";
import myMedicalPaOrdersSlice from "../slice/medicalPa/myPaOrdersSlice";
import narMedicalPaOrderSlice from "../slice/medicalPa/narSlice";
import notToWorkFedoraMedicalPaOrderSlice from "../slice/medicalPa/notToWorkFedoraPaOrdersSlice";
import notToWorkMedicalPaOrderSlice from "../slice/medicalPa/notToWorkPaOrdersSlice";
import notToWorkStatCaseMedicalPaOrderSlice from "../slice/medicalPa/notToWorkStatCaseSlice";
import pendingMedicalPaOrderSlice from "../slice/medicalPa/pendingPaOrdersSlice";
import pharmFillMedicalPaOrdersSlice from "../slice/medicalPa/pharmFillPaOrdersSlice";
import podMedicalPaOrderSlice from "../slice/medicalPa/podPaOrdersSlice";
import { postgresFacetCountsSlice } from "../slice/medicalPa/postgresFacetsCountsSlice";
import queryMedicalPaOrdersSlice from "../slice/medicalPa/queryPaOrderSlice";
import reviewPendingCountSlice from "../slice/medicalPa/reviewPendingCountSlice";
import singleMedicalPaFormConfigSlice from "../slice/medicalPa/singleMedicalPaFormConfig";
import singleMedicalPaOrderSlice from "../slice/medicalPa/singleMedicalPaOrder";
import medicalPaCoveragerawResponseSlice from "../slice/medicalPaCoveragerawResponseSlice";
import medicineNameConfigSlice from "../slice/medicineNameConfigSlice";
import memberSlice from "../slice/member-slice";
import modalSlice from "../slice/modalSlice";
import nccnIcdConfigSlice from "../slice/nccnIcdConfigSlice";
import nccnRegimenConfigSlice from "../slice/nccnRegimenConfigSlice";
import nestedDocumentsSlice from "../slice/nestedDocumentsSlice";
import nycbsMedicalPaPatientsSlice from "../slice/nycbsMedicalPaPatientsSlice";
import nycbsDocumentsSlice from "../slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import nycbsPharmaCompletedOrder from "../slice/nycbsPharmaExternal/nycbsPharmaCompletedOrder";
import nycbsPharmaOrder from "../slice/nycbsPharmaExternal/nycbsPharmaOrder";
import oncoemrUsersSlice from "../slice/oncoemrUsersSlice";
import ordersForPatientSlice from "../slice/ordersForPatientSlice";
import errorOrdersDetailsSlice from "../slice/ordersSlice/error-order-slice";
import nycbsClaimsPaSlice from "../slice/ordersSlice/nycbsClaimsPaSlice";
import nycbsMedicalPaSlice from "../slice/ordersSlice/nycbsMedicalPaSlice";
import orderSlice from "../slice/ordersSlice/order-slice";
import paOrdersDetailsSlice from "../slice/ordersSlice/pa-order-slice";
import organizationSlice from "../slice/organizationSlice";
import { paFormConfigurationSlice } from "../slice/paFormConfigSlice";
import parsedInsuranceSlice from "../slice/parsedInsuranceSlice";
import patientResponsibilityArrayCount from "../slice/patient-responsibility/patient-responsibility-array-count";
import patientResponsibilitySlice from "../slice/patient-responsibility/patient-responsibility-slice";
import patientResponsibilitySummarySlice from "../slice/patient-responsibility/patient-responsibility-summary-slice";
import patientsSlice from "../slice/patients-slice";
import { pbmConfigurationsSlice } from "../slice/pbmConfigurationSlice";
import { prescriptionParsingConfigurationSlice } from "../slice/prescriptionParsingConfigSlice";
import providerDetailsSlice from "../slice/providerDetailsSlice";
import providerSlice from "../slice/providerSlice";
import rpaConfigurationSlice from "../slice/rpaConfiguration";
import singleOrderSlice from "../slice/single-order-slice";
import singleHealthFacilityDetailsSlice from "../slice/singleHealthFacilityDetailsSlice";
import singlePatientSlice from "../slice/singlePatientSlice";
import submissionDocsSlice from "../slice/submissionDocsSlice";
import superAdminProvidersSlice from "../slice/superAdminProvidersSlice";
import viewAllAuditTrailSlice from "../slice/viewAllAuditTrailSlice";
import workflowConfigSlice from "../slice/workflowConfigSlice";

/** Generic async-data state wrapper used by slices that follow a loading/error/data pattern. */
export interface DataState<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

const store = configureStore({
  reducer: {
    counter: counterSlice,
    order: orderSlice,
    errorOrders: errorOrdersDetailsSlice,
    paOrders: paOrdersDetailsSlice,
    singleOrder: singleOrderSlice,
    patients: patientsSlice,
    singlePatient: singlePatientSlice,
    ordersForPatient: ordersForPatientSlice,
    firebaseAuthentication: firebaseAuthSlice,
    providerDetails: providerDetailsSlice,
    allProviders: providerSlice,
    member: memberSlice,
    fileUpload: fileUploadSlice,
    patientResponsibility: patientResponsibilitySlice,
    allCoveragesForOrderSlice: allCoveragesForOrderSlice,
    patientResponsibilitySummary: patientResponsibilitySummarySlice,
    patientResponsibilityArrayCount: patientResponsibilityArrayCount,
    cptSlice: allCptSlice,
    facilitySlice: facilitySlice,
    pharmaQuestions: pharmaQuestionsSlice,
    cmmOrders: cmmOrdersSlice,
    cmmSingleOrder: cmmSingleOrderSlice,
    formOptions: formOptionsSlice,
    cmmFormConfig: cmmFormConfig,
    availityServiceReviewConfiguration: availityServiceReviewConfigurationSlice,
    submissionDocs: submissionDocsSlice,
    formQuestions: formQuestionsSlice,
    nycbsPharmaOrders: nycbsPharmaOrder,
    nycbsPharmaCompletedOrders: nycbsPharmaCompletedOrder,
    nycbsDocuments: nycbsDocumentsSlice,
    prescription: prescriptionSlice,
    insuranceDetails: insuranceDetailsSlice,
    diagnosisDetails: diagnosisDetailsSlice,
    patientEligibility: patientEligibilitySlice,
    nycbsMedicalPa: nycbsMedicalPaSlice,
    nycbsClaimsPa: nycbsClaimsPaSlice,
    nycbsMedicalPaPatients: nycbsMedicalPaPatientsSlice,
    filterValues: filterValuesSlice,
    cmmProcessedOrders: cmmProcessedOrdersSlice,
    medicalPaOrders: medicalPaOrdersSlice,
    singleMedicalPaOrder: singleMedicalPaOrderSlice,
    medicalPaCoveragerawResponse: medicalPaCoveragerawResponseSlice,
    myMedicalPaOrders: myMedicalPaOrdersSlice,
    authorizedMedicalPaOrders: authorizedMedicalPaOrdersSlice,
    financialMedicalPaOrders: financialMedicalPaOrdersSlice,
    holdMedicalPaOrders: holdMedicalPaOrderSlice,
    pharmFillMedicalPaOrders: pharmFillMedicalPaOrdersSlice,
    queryMedicalPaOrders: queryMedicalPaOrdersSlice,
    cmmDiffData: cmmDiffDataSlice,
    cmmFormDiffTableData: cmmFormDiffTableDataSlice,
    documentsConfiguration: documentsConfigurationSlice,
    nestedDocuments: nestedDocumentsSlice,
    organization: organizationSlice,
    medicalPaClinicalData: medicalPaClinicalDataSlice,
    medicalPaFormConfig: medicalPaFormConfigSlice,
    rpaConfiguration: rpaConfigurationSlice,
    formConfig: formConfigSlice,
    singleMedicalPaFormConfig: singleMedicalPaFormConfigSlice,
    nycbsSentToPlanConfig: nycbsSentToPlanSlice,
    pbmConfigurations: pbmConfigurationsSlice,
    drugConfig: drugConfigSlice,
    parsedInsurance: parsedInsuranceSlice,
    credentialsConfig: credentialsConfigSlice,
    credentialDetails: credentialDetailsSlice,
    uniqueDrugname: uniqueDrugnameSlice,
    uniqueFormName: uniqueFormNameSlice,
    healthFacility: healthFacilitySlice,
    nccnIcdConfig: nccnIcdConfigSlice,
    nccnRegimenConfig: nccnRegimenConfigSlice,
    clientConfiguration: clientConfigurationSlice,
    paFormConfig: paFormConfigurationSlice,
    medicineNameConfig: medicineNameConfigSlice,
    prescriptionParsingConfig: prescriptionParsingConfigurationSlice,
    finalWorklistOrdersData: finalWorklistOrdersSlice,
    analyticsData: analyticsDataSlice,
    modal: modalSlice,
    authOnFileMedicalPaOrders: authOnFileMedicalPaOrdersSlice,
    narMedicalPaOrders: narMedicalPaOrderSlice,
    pendingMedicalPaOrders: pendingMedicalPaOrderSlice,
    deniedMedicalPaOrders: deniedMedicalPaOrderSlice,
    notToWorkMedicalPaOrders: notToWorkMedicalPaOrderSlice,
    notToWorkFedoraMedicalPaOrders: notToWorkFedoraMedicalPaOrderSlice,
    notToWorkStatCaseMedicalPaOrders: notToWorkStatCaseMedicalPaOrderSlice,
    boOptions: boOptionsSlice,
    estimateOptions: estimateOptionsSlice,
    podMedicalPaOrders: podMedicalPaOrderSlice,
    authStatusOptions: authStatusOptionsSlice,
    auditTrialOverview: auditTrialOverviewSlice,
    auditTrial: auditTrialSlice,
    workflowConfig: workflowConfigSlice,
    formConfigNoLiveListen: formConfigNoLiveListenSlice,
    genericAuthStatusTable: genericAuthStatusTableSlice,
    viewAllAuditTrail: viewAllAuditTrailSlice,
    externalTablePoweredByAlgolia: externalTablePoweredByAlgoliaSlice,
    medicalPaOrdersAlgolia: medicalPaOrdersAlgoliaSlice,
    algoliaFacetsCount: algoliaFacetsCountSlice,
    algoliaMultiFacetsCount: algoliaMultiFacetsCountSlice,
    superAdminProviders: superAdminProvidersSlice,
    reviewPendingCount: reviewPendingCountSlice,
    singleHealthFacilityDetails: singleHealthFacilityDetailsSlice,
    guidelinesDocuments: guidelinesDocumentsSlice.reducer,
    medicalPaOrdersFromPostgres: medicalPaOrdersFromPostgresSlice,
    postgresFacetCounts: postgresFacetCountsSlice.reducer,
    oncoemrUsers: oncoemrUsersSlice,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export default store;

/** Typed dispatch for use with `useDispatch` hook. */
export type AppDispatch = typeof store.dispatch;

/** Inferred root state type derived from the store's reducer map. */
export type RootState = ReturnType<typeof store.getState>;

/** Typed thunk action for async middleware. */
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
